import { DynamicPageVariant } from '@betterfunnel/types';
import {
  collection,
  doc,
  orderBy,
  query,
  UpdateData,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';
import { updateDynamicPageHasPendingPublishChanges } from './DynamicPagesService';

export const dynamicPageVariantsColRef = (
  accountId: string,
  dynamicPageId: string,
) =>
  collection(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
    'variants',
  ).withConverter(emptyConverter<DynamicPageVariant>());

export const getDynamicPageVariantsQuery = (
  accountId: string,
  dynamicPageId: string,
) =>
  query(
    dynamicPageVariantsColRef(accountId, dynamicPageId),
    where('status', 'in', ['draft', 'active', 'disabled']),
    orderBy('createdAt', 'asc'),
  );

export const duplicateWebsiteAsVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  websiteId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts-campaigns-experiments-duplicateWebsiteAsVariant',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    websiteId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const startExperiment = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts-campaigns-experiments-startExperiment',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const endExperiment = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  winnerWebsiteId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts-campaigns-experiments-endExperiment',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    winnerWebsiteId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const duplicateWebsiteInVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  websiteId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts-campaigns-experiments-duplicateWebsiteInVariant',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    websiteId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const removeWebsiteFromVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  websiteId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts-campaigns-experiments-removeWebsiteFromVariant',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    websiteId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const duplicateVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
): Promise<any> => {
  const fn = httpsCallable(functions, 'accounts-campaigns-duplicateVariant');

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const duplicateVariantAsNewDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts-campaigns-duplicateVariantAsNewDynamicPage',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
  });

  return res.data;
};

export const updateVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  update: UpdateData<DynamicPageVariant>,
): Promise<any> => {
  const varaintDocRef = doc(
    dynamicPageVariantsColRef(accountId, dynamicPageId),
    variantId,
  );

  await updateDoc(varaintDocRef, update);

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);
};
